const TOKEN_RE = /\[(?:Image|Video|Audio|File)#\d+\]/g;

// A snapshot row dragged out of the library carries its file name under this
// type. Dropping it on the agent's prompt writes the name there, which is the
// whole point: "make a copy of <name>" is otherwise typed out by hand.
export const SNAPSHOT_DRAG_TYPE = "application/x-playin-snapshot";

// WHAT IS BEING DRAGGED, remembered outside the DOM.
//
// `dataTransfer` alone was not enough. The library that draws the snapshot
// tree makes the row's <li> the drag source, so the name has to be fished out
// of the DOM in a dragstart handler further up — and when that read missed,
// nothing was set, the composer's dragover never called preventDefault, and
// the browser refused the drop without a word. This is set by the row itself,
// on mousedown, where the name is simply in hand.
let draggedName = null;

/** The row the pointer went down on; null for a folder or anything else. */
export function setDraggedSnapshot(name) {
  draggedName = name || null;
}

export function draggedSnapshot() {
  return draggedName;
}

/** `text` with `addition` at its end, separated by a space when one is needed. */
export function appendToPrompt(text, addition) {
  const current = String(text || "");
  const word = String(addition || "").trim();
  if (!word) return current;
  return `${current}${current && !/\s$/.test(current) ? " " : ""}${word}`;
}

export function attachmentTokenRanges(text) {
  const ranges = [];
  for (const match of String(text || "").matchAll(TOKEN_RE)) {
    ranges.push({ start: match.index, end: match.index + match[0].length });
  }
  return ranges;
}

/** Return every attachment reference present before an edit but absent after it. */
export function removedAttachmentReferences(previousText, nextText) {
  const next = String(nextText || "");
  return [...new Set(
    String(previousText || "").match(TOKEN_RE) || []
  )].filter((reference) => !next.includes(reference));
}

export function expandAttachmentTokenSelection(text, start, end) {
  let expandedStart = start;
  let expandedEnd = end;
  for (const token of attachmentTokenRanges(text)) {
    if (token.start < expandedEnd && token.end > expandedStart) {
      expandedStart = Math.min(expandedStart, token.start);
      expandedEnd = Math.max(expandedEnd, token.end);
    }
  }
  return { start: expandedStart, end: expandedEnd };
}

/** If a click places the caret in a token, return the token's end position. */
export function attachmentTokenEndAtCaret(text, position) {
  const token = attachmentTokenRanges(text).find((item) => position >= item.start && position < item.end);
  return token ? token.end : null;
}

/** Return the edited value/caret when Backspace or Delete touches an attachment token. */
export function deleteAttachmentTokenAtCaret(text, start, end, direction) {
  const value = String(text || "");
  let range;
  if (start !== end) {
    range = expandAttachmentTokenSelection(value, start, end);
  } else {
    const token = attachmentTokenRanges(value).find((item) => direction === "backward"
      ? item.start < start && item.end >= start
      : item.start <= start && item.end > start);
    if (!token) return null;
    range = token;
  }
  if (range.start === range.end) return null;
  return {
    value: `${value.slice(0, range.start)}${value.slice(range.end)}`,
    caret: range.start,
  };
}
